import {TabView} from "primereact/tabview";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {matchPath, Outlet, useLocation} from "react-router-dom";

import AppreciationToolbar from "../component/appreciation/AppreciationToolbar";
import {MainFrame, MainToolbar} from "../component/MainFrame";
import MissingPermissionIndicator from "../component/MissingPermissionIndicator";
import {getTabPanelWithPermission} from "../component/TabPanelWithPermission";
import {useNavigateWithQuery} from "../hooks/navigationHooks";
import {selectPermissions} from "../redux/authSlice";
import {useEffectRequiresValidRevaId} from "../util/hooks";
import {
    checkPermissionRule,
    PERMISSION_RULE_MARKET,
    PERMISSION_RULE_MARKET_MARKETDATA,
    PERMISSION_RULE_MARKET_PRICEINDEX,
    PERMISSION_RULE_MARKET_TIMELINES,
} from "../util/permissions";

/** @type {TabHeaderWithPermissionParams[]} */
const TABS = [
    {
        route: "description",
        headerI18n: "market.category.description",
    },
    {
        route: "timelines",
        headerI18n: "market.category.timelines",
        permissionRule: PERMISSION_RULE_MARKET_TIMELINES,
        appreciationKeyTab: "timelines",
    },
    {
        route: "priceindex",
        headerI18n: "market.category.priceindex",
        permissionRule: PERMISSION_RULE_MARKET_PRICEINDEX,
    },
    {
        route: "market-data",
        headerI18n: "market.category.market_data",
        permissionRule: PERMISSION_RULE_MARKET_MARKETDATA,
    },
];
const TAB_ROUTES = TABS.map((tab) => tab.route);

/**
 * @returns {React.JSX.Element}
 */
function PageMarket() {
    const {t} = useTranslation();

    const navigate = useNavigateWithQuery();
    const location = useLocation();
    const {pathname} = location;

    const permissions = useSelector(selectPermissions);

    const [appreciationForTabVisible, setAppreciationForTabVisible] = useState(false);

    /** @type React.ReactElement[] */
    const tabs = TABS.map((tab) => getTabPanelWithPermission(tab, t, permissions));

    useEffectRequiresValidRevaId();

    let activeIndex = null;
    for (let index = 0; index < TAB_ROUTES.length; index++) {
        if (matchPath(`/market/${TAB_ROUTES[index]}`, pathname)) {
            activeIndex = index;
            break;
        }
    }
    const appreciationKeyTab = TABS[activeIndex].appreciationKeyTab;

    const onTabChange = (index) => {
        if (index >= 0 && index < TAB_ROUTES.length) {
            setAppreciationForTabVisible(false);
            navigate(`/market/${TAB_ROUTES[index]}`);
        }
    };

    const hasAnyPermissions = checkPermissionRule(permissions, PERMISSION_RULE_MARKET);
    if (!hasAnyPermissions) {
        return <MissingPermissionIndicator missingPermissions={true} />;
    }

    return (
        <MainFrame>
            <MainToolbar title={t("market.market")}>
                <TabView
                    className="flex-1 HeaderTabView"
                    panelContainerClassName="hidden"
                    activeIndex={activeIndex}
                    onTabChange={(e) => onTabChange(e.index)}
                >
                    {tabs}
                </TabView>
                <AppreciationToolbar
                    page="market"
                    tab={appreciationKeyTab}
                    appreciationVisible={appreciationForTabVisible}
                    setAppreciationVisible={setAppreciationForTabVisible}
                />
            </MainToolbar>
            <Outlet
                context={{
                    appreciation: {
                        page: "market",
                        tab: appreciationKeyTab,
                        visible: appreciationForTabVisible,
                    },
                }}
            />
        </MainFrame>
    );
}

export default PageMarket;
